import { Breadcrumbs, Typography, Link, Tooltip } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ApartmentIcon from "@mui/icons-material/Apartment";
import BusinessIcon from "@mui/icons-material/Business";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useProjectStore } from "../../store/projectStore";
import { useTranslation } from "react-i18next";
import { useClientProjectById } from "../../utils/hooks/useClientProjects";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../utils/api";
import { API_ROUTES } from "../../routes/apiRoutes";
import { useEffect } from "react";

const shimmerStyle = {
  display: "inline-block",
  width: 72,
  height: 12,
  borderRadius: 3,
  background: "linear-gradient(90deg, #e0e0e0 25%, #efefef 50%, #e0e0e0 75%)",
  backgroundSize: "200% 100%",
  animation: "breadcrumb-shimmer 1.4s infinite",
  verticalAlign: "middle",
};

const BreadcrumbNav = ({ name, isPaddingReq = true, items = null }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const projectStoreName = useProjectStore((state) => state.projectName);
  const projectStoreId = useProjectStore((state) => state.projectId);
  const buildingStoreName = useProjectStore((state) => state.buildingName);
  const setProject = useProjectStore((state) => state.setProject);
  const setBuildingName = useProjectStore((state) => state.setBuildingName);
  const clearProject = useProjectStore((state) => state.clearProject);

  const projectIdFromParams = params.id;
  const projectIdFromSearch = searchParams.get("projectId");
  const projectId = projectIdFromSearch || projectIdFromParams || projectStoreId;

  const buildingId = params.buildingId || searchParams.get("buildingId") || null;
  const overviewImageId = searchParams.get("overviewImageId") || null;

  const isProjectsActive = location.pathname === "/dashboard/projects";
  const isProjectActive =
    location.pathname.startsWith("/dashboard/projects/") && !!projectId && !buildingId;
  const isBuildingActive =
    (location.pathname.includes("/dashboard/building/information") ||
      location.pathname.includes("/dashboard/buildings/overview") ||
      location.pathname.includes("/dashboard/building/maintainence") ||
      location.pathname.includes("/dashboard/building/finance") ||
      location.pathname.includes("/dashboard/building/overview")) &&
    !!buildingId;

  const showProjectSegment = projectId && !isProjectsActive && location.pathname !== "/dashboard";

  const needsProjectHydration = !items && showProjectSegment && !projectStoreName && !!projectId;
  const { data: projectData } = useClientProjectById(needsProjectHydration ? projectId : null);

  useEffect(() => {
    if (projectData?.projectName && !projectStoreName) {
      setProject(projectId, projectData.projectName);
    }
  }, [projectData, projectStoreName, projectId, setProject]);

  const needsBuildingHydration = !items && !!buildingId && !buildingStoreName && !!projectId;
  const { data: buildingsData } = useQuery({
    queryKey: ["buildings-breadcrumb", projectId],
    queryFn: async () => {
      const response = await api.get(API_ROUTES.BUILDING, {
        params: { projectId, page: 1 },
      });
      return response.data.data || [];
    },
    enabled: needsBuildingHydration,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    if (buildingsData && !buildingStoreName && buildingId) {
      const match = buildingsData.find((b) => String(b.buildingId ?? b.id) === String(buildingId));
      if (match?.name) {
        setBuildingName(match.name);
      }
    }
  }, [buildingsData, buildingStoreName, buildingId, setBuildingName]);

  const effectiveProjectName = projectStoreName || projectData?.projectName;
  const effectiveBuildingName = name || buildingStoreName;

  const handleClick = (path) => {
    if (path === "/dashboard/projects" || path === "/dashboard") {
      clearProject();
    }
    navigate(path);
  };

  const goToProject = () => {
    if (!projectId) {
      navigate("/dashboard/projects");
      return;
    }
    navigate(`/dashboard/projects/${projectId}`);
  };

  if (items) {
    return (
      <div className={`mb-2 mt-2 ${isPaddingReq ? "px-4" : ""}`}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const isClickable = !!item.path && !isLast;

            if (isClickable) {
              return (
                <Tooltip key={index} title={item.label || ""} arrow>
                  <Link
                    underline="hover"
                    color="inherit"
                    onClick={() => handleClick(item.path)}
                    className="flex items-center gap-1 cursor-pointer"
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                </Tooltip>
              );
            }

            return (
              <span
                key={index}
                className="flex items-center gap-1 font-bold"
                style={{ color: "#3D6C50", cursor: "default" }}
              >
                {item.icon}
                {item.label ? (
                  <Tooltip title={item.label} arrow>
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </span>
                  </Tooltip>
                ) : (
                  <span style={shimmerStyle} />
                )}
              </span>
            );
          })}
        </Breadcrumbs>

        <style>{`
          @keyframes breadcrumb-shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className={`mb-2 mt-2 ${isPaddingReq ? "px-4" : ""}`}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
        <Tooltip title="Dashboard" arrow>
          <Link
            underline="hover"
            color={location.pathname === "/dashboard" ? "primary" : "inherit"}
            onClick={() => handleClick("/dashboard")}
            className={`flex items-center gap-1 cursor-pointer${location.pathname === "/dashboard" ? " font-bold" : ""}`}
          >
            <HomeIcon fontSize="small" />
            {t("main.sidebar.dashboard")}
          </Link>
        </Tooltip>

        <Tooltip title="Projects" arrow>
          <Link
            underline="hover"
            color={isProjectsActive ? "#3D6C50" : "inherit"}
            onClick={() => handleClick("/dashboard/projects")}
            className={`flex items-center gap-1 cursor-pointer${isProjectsActive ? " font-bold" : ""}`}
          >
            <ApartmentIcon fontSize="small" />
            {t("main.sidebar.assets")}
          </Link>
        </Tooltip>

        {showProjectSegment && (
          <Tooltip title="Project Name" arrow>
            <Link
              underline="hover"
              color={isProjectActive ? "#3D6C50" : "inherit"}
              onClick={goToProject}
              className={`flex items-center gap-1 cursor-pointer${isProjectActive ? " font-bold" : ""}`}
            >
              <BusinessIcon fontSize="small" />
              {effectiveProjectName || <span style={shimmerStyle} />}
            </Link>
          </Tooltip>
        )}
        {buildingId && (
          <Tooltip title="Building Name" arrow>
            <span
              className={`flex items-center gap-1${isBuildingActive ? " font-bold" : ""}`}
              style={
                isBuildingActive ? { color: "#3D6C50", cursor: "default" } : { cursor: "default" }
              }
            >
              <LocationCityIcon fontSize="small" />
              {effectiveBuildingName || <span style={shimmerStyle} />}
            </span>
          </Tooltip>
        )}
      </Breadcrumbs>

      <style>{`
        @keyframes breadcrumb-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
};

export default BreadcrumbNav;
